import dagre from "dagre";

// Reasonable node-size guesses (matches our node card widths)
const NODE_W = { roleNode: 240, departmentNode: 270, processNode: 270, systemNode: 240 };
const NODE_H = 140;

/**
 * Auto-layout React Flow nodes + edges using dagre.
 *
 * @param {Array} nodes  React Flow nodes
 * @param {Array} edges  React Flow edges
 * @param {"TB"|"LR"} direction  TB = top→bottom (org chart), LR = left→right (flow)
 * @returns {{nodes:Array, edges:Array}}
 */
export function autoLayout(nodes, edges, direction = "TB") {
  if (!nodes || nodes.length === 0) return { nodes, edges };

  const g = new dagre.graphlib.Graph();
  g.setGraph({
    rankdir: direction,
    nodesep: 70,
    ranksep: 110,
    marginx: 40,
    marginy: 40,
  });
  g.setDefaultEdgeLabel(() => ({}));

  nodes.forEach((n) => {
    g.setNode(n.id, { width: NODE_W[n.type] || 240, height: NODE_H });
  });
  edges.forEach((e) => g.setEdge(e.source, e.target));

  dagre.layout(g);

  const isLR = direction === "LR";
  const sourceHandle = isLR ? "r" : "b";
  const targetHandle = isLR ? "l-in" : "t-in";

  const laidOutNodes = nodes.map((n) => {
    const pos = g.node(n.id);
    return {
      ...n,
      position: {
        x: (pos?.x ?? 0) - (NODE_W[n.type] || 240) / 2,
        y: (pos?.y ?? 0) - NODE_H / 2,
      },
      sourcePosition: isLR ? "right" : "bottom",
      targetPosition: isLR ? "left" : "top",
    };
  });

  const laidOutEdges = edges.map((e) => ({
    ...e,
    sourceHandle,
    targetHandle,
    type: "smoothstep",
  }));

  return { nodes: laidOutNodes, edges: laidOutEdges };
}

/**
 * Detect if every node in a list is essentially at the origin (0,0) — meaning
 * the user has never positioned anything. Used to auto-apply a layout on load.
 */
export function needsAutoLayout(nodes) {
  if (!nodes || nodes.length === 0) return false;
  return nodes.every((n) => {
    const x = n.position?.x ?? 0;
    const y = n.position?.y ?? 0;
    return Math.abs(x) < 1 && Math.abs(y) < 1;
  });
}
